import React, {useState, useRef} from "react";
import {Box, Button, Typography, Avatar, IconButton, Paper} from "@mui/material";
import {CloudUpload, Delete, Visibility} from "@mui/icons-material";

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onRemove?: () => void;
  accept?: string;
  maxSize?: number; // in bytes
  label?: string;
  currentFileUrl?: string;
  currentFileName?: string;
  disabled?: boolean;
  showPreview?: boolean;
  previewSize?: number;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  onRemove,
  accept = "image/*",
  maxSize = 5 * 1024 * 1024, // 5MB default
  label = "Upload File",
  currentFileUrl,
  currentFileName,
  disabled = false,
  showPreview = true,
  previewSize = 100,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setError(null);

    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    // Validate file size
    if (file.size > maxSize) {
      setError(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
      return;
    }

    // Validate file type
    if (accept !== "*/*" && !file.type.match(accept.replace("*", ".*"))) {
      setError(`File type not allowed. Accepted types: ${accept}`);
      return;
    }

    onFileSelect(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setError(null);
  };

  const getFileIcon = () => {
    if (currentFileUrl && currentFileUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
      return <Avatar src={currentFileUrl} alt={currentFileName || "File preview"} sx={{width: previewSize, height: previewSize, mb: 2}} />;
    }
    return <CloudUpload sx={{fontSize: 48, color: "primary.main", mb: 2}} />;
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: "center",
          border: dragActive ? "2px dashed" : "1px solid",
          borderColor: dragActive ? "primary.main" : "divider",
          backgroundColor: dragActive ? "action.hover" : "background.paper",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          transition: "all 0.2s ease-in-out",
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={disabled ? undefined : handleClick}>
        <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileInput} style={{display: "none"}} disabled={disabled} />

        {currentFileUrl && showPreview ? (
          <Box sx={{position: "relative", display: "inline-block"}}>
            {getFileIcon()}
            <Box sx={{position: "absolute", top: 0, right: 0}}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(currentFileUrl, "_blank");
                }}
                sx={{bgcolor: "rgba(255,255,255,0.8)", "&:hover": {bgcolor: "rgba(255,255,255,0.9)"}}}>
                <Visibility />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <CloudUpload sx={{fontSize: 48, color: "primary.main", mb: 2}} />
        )}

        <Typography variant="h6" gutterBottom>
          {currentFileName || label}
        </Typography>

        {!currentFileUrl && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            Drag and drop a file here, or click to select
          </Typography>
        )}

        {currentFileUrl && (
          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            Current file: {currentFileName || "Uploaded file"}
          </Typography>
        )}

        <Box sx={{display: "flex", gap: 1, justifyContent: "center"}}>
          <Button
            variant="contained"
            component="span"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}>
            {currentFileUrl ? "Change File" : "Select File"}
          </Button>

          {currentFileUrl && onRemove && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}>
              Remove
            </Button>
          )}
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{mt: 1}}>
            {error}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: "block"}}>
          Accepted formats: {accept} | Max size: {Math.round(maxSize / 1024 / 1024)}MB
        </Typography>
      </Paper>
    </Box>
  );
};

export default FileUpload;
